import React, { useState } from 'react';
import {
  MarketingLayout,
  Button,
  Box,
  Text,
  Divider,
  TextButton,
} from 'wix-style-react';
import { Play, ChevronDown } from 'wix-ui-icons-common';
import {
  translate,
  InjectedTranslateProps,
  Trans,
  InjectedI18nProps,
} from 'react-i18next';
import * as css from './Banner.scss';
import { ModuleRegistry } from 'react-module-container';
import { CrmButtonAction } from '../../types';

import { BMComponentNames } from '../constants';

export interface BannerProps extends InjectedTranslateProps, InjectedI18nProps {
  title: string;
  decription?: string;
  mainActionTitle?: string;
  secActionTitle?: string;
  video?: string;
  image?: string;
  onOpenVideoModal?(): void;
  onMainActionClick?(payload?: any): void;
  onSecActionClicked?(payload?: any): void;
  mainButtonConfig?: CrmButtonAction;
  secondaryButtonConfig?: CrmButtonAction;
}

const BannerComponent: React.FC<BannerProps> = (props) => {
  const [pickerReady, setPickerReady] = useState(false);

  const ContactPicker = ModuleRegistry.component(
    BMComponentNames.contactPicker,
  );

  const listener = ModuleRegistry.addListener(
    'reactModuleContainer.componentReady',
    (component) => {
      if (component === 'contact-picker-popover-lazy') {
        setPickerReady(true);
        listener.remove();
      }
    },
  );

  const renderBannerActions = () => {
    const { video, t, i18n } = props;
    return (
      <Box direction="vertical" width="414px">
        <Box direction="horizontal">
          <Box marginRight="12px" marginBottom="23px">
            {renderActionButton({
              dataHook: 'crm-home-banner-main-action',
              onClick: props.onMainActionClick!,
              title: props.mainActionTitle!,
              config: props.mainButtonConfig!,
            })}
          </Box>
          {props.secActionTitle && renderSecondaryAction()}
        </Box>
        {video && [
          <Divider />,
          <Box marginTop="15px">
            <Play />
            <Text size="small" className={css.watchVideo}>
              <Trans
                defaults={t('banner.common.actions.hint')}
                i18n={i18n}
                t={t}
                components={[
                  <TextButton
                    dataHook="crm-home-banner-video-btn"
                    key={1}
                    onClick={props.onOpenVideoModal}
                    size="small"
                  >
                    TBD
                  </TextButton>,
                ]}
              />
            </Text>
          </Box>,
        ]}
      </Box>
    );
  };

  const renderSecondaryAction = () => {
    return (
      <Box>
        {renderActionButton({
          secondary: true,
          dataHook: 'crm-home-banner-sec-action',
          onClick: props.onSecActionClicked!,
          title: props.secActionTitle!,
          config: props.secondaryButtonConfig!,
        })}
      </Box>
    );
  };

  const renderActionButton = ({
    secondary = false,
    dataHook,
    onClick,
    title,
    config,
  }: {
    secondary?: boolean;
    dataHook: string;
    onClick(contactId?: string): any;
    title: string;
    config: CrmButtonAction;
  }) => {
    const renderContactPicker = config.value.withContactId;

    const buttonWithNoAction = (
      <Box width="212px">
        <Button
          width=""
          priority={secondary ? 'secondary' : undefined}
          dataHook={dataHook}
          suffixIcon={<ChevronDown />}
          fullWidth
        >
          {title}
        </Button>
      </Box>
    );

    return renderContactPicker ? (
      <Box width="212px" height="36px">
        {!pickerReady && buttonWithNoAction}
        <Box display={pickerReady ? 'block' : 'none'}>
          <ContactPicker
            onSelect={(contact) => {
              onClick && onClick(contact.id);
            }}
            moveBy={{ x: '10px', y: '2px' }}
            wrappedButton={buttonWithNoAction}
            contentWidth={273} // TODO not working on popover
            fixed={true}
            flip={false}
            appendTo="window"
          />
        </Box>
      </Box>
    ) : (
      <Button
        priority={secondary ? 'secondary' : undefined}
        dataHook={dataHook}
        onClick={() => onClick()}
      >
        {title}
      </Button>
    );
  };

  const { title, decription, image } = props;
  return (
    <MarketingLayout
      dataHook="crm-home-banner"
      title={title}
      description={decription}
      image={image}
      actions={renderBannerActions()}
      size="medium"
    />
  );
};

export const Banner = translate()(BannerComponent);
